//Produtos

import React, { useEffect, useState, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import api from '../services/api';
import toast, { Toaster } from 'react-hot-toast';
import {  
    ChevronLeft, Plus, Trash2, Home, ListChecks,  
    ChevronDown, LayoutGrid, Coffee, AlertTriangle, UploadCloud, FileText, Table, Webhook, HelpCircle, CheckCircle2
} from 'lucide-react';
import '../styles/Dashboard.css';

export default function Produtos() {
    const { id } = useParams();
    const navigate = useNavigate();
    const fileInputRef = useRef(null);
    const importFileInputRef = useRef(null);

    const [produtos, setProdutos] = useState([]);
    const [categorias, setCategorias] = useState([]);
    const [gruposDisponiveis, setGruposDisponiveis] = useState([]); 
    const [loading, setLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showImportModal, setShowImportModal] = useState(false);
    
    const [editandoId, setEditandoId] = useState(null);
    const [itemParaExcluir, setItemParaExcluir] = useState(null);
    
    const [imagePreview, setImagePreview] = useState(null);
    const [uploadFile, setUploadFile] = useState(null);
    const [importTipo, setImportTipo] = useState('csv');
    const [importandoLoading, setImportandoLoading] = useState(false);

    const initialState = {
        nome: '',
        descricao: '',
        preco: '',
        estoque: 0,
        categoria_id: '',
        imagem_url: '',
        ativo: 1,
        destaque: 0,
        adicionais_ids: [] 
    };

    const [novoProduto, setNovoProduto] = useState(initialState);

    // Identifica se a categoria atual é bebida para esconder opcionais
    const isBebida = () => {
        const cat = categorias.find(c => String(c.id) === String(novoProduto.categoria_id));
        const nome = cat ? cat.nome.toLowerCase() : '';
        return nome.includes('bebida') || nome.includes('refr') || nome.includes('suco') || nome.includes('cerveja');
    };

    const carregarDados = async () => {
        setLoading(true);
        try {
            const [resProdutos, resCategorias, resAdicionais] = await Promise.all([
                api.get(`/cardapio/produtos/${id}`),
                api.get(`/cardapio/categorias/${id}`),
                api.get(`/cardapio/adicionais/${id}`) 
            ]);

            setProdutos(resProdutos.data);
            setCategorias(resCategorias.data);
            
            const gruposTratados = resAdicionais.data.map(g => ({
                ...g,
                itens: typeof g.itens_json === 'string' ? JSON.parse(g.itens_json) : (g.itens_json || [])
            }));
            
            setGruposDisponiveis(gruposTratados);

            if (resCategorias.data.length > 0 && !editandoId) {
                setNovoProduto(prev => ({ ...prev, categoria_id: resCategorias.data[0].id }));
            }
        } catch (e) {
            toast.error("Erro ao carregar dados.");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { if (id) carregarDados(); }, [id]);

    const toggleGrupo = (grupoId) => {
        setNovoProduto(prev => {
            const ids = [...prev.adicionais_ids];
            const index = ids.indexOf(grupoId);
            if (index > -1) ids.splice(index, 1);
            else ids.push(grupoId);
            return { ...prev, adicionais_ids: ids };
        });
    };

    const handleExcluirProduto = async () => {
        try {
            await api.delete(`/cardapio/produtos/${itemParaExcluir.id}`);
            toast.success("Produto excluído com sucesso!");
            setShowDeleteModal(false);
            carregarDados();
        } catch (e) {
            toast.error("Erro ao excluir produto.");
        }
    };

    const fecharModal = () => {
        setShowModal(false);
        setEditandoId(null);
        setNovoProduto(initialState);
        setImagePreview(null);
        setUploadFile(null);
    };

    const handleSalvarProduto = async (e) => {
        e.preventDefault();
        const formData = new FormData();
        formData.append('estabelecimento_id', id);
        formData.append('nome', novoProduto.nome);
        formData.append('descricao', novoProduto.descricao || '');
        formData.append('preco', parseFloat(String(novoProduto.preco).replace(',', '.')));
        formData.append('estoque', novoProduto.estoque);
        formData.append('categoria_id', novoProduto.categoria_id);
        formData.append('ativo', novoProduto.ativo);
        formData.append('destaque', novoProduto.destaque);
        
        const idsFinais = isBebida() ? [] : novoProduto.adicionais_ids;
        formData.append('adicionais', JSON.stringify(idsFinais));
        
        if (uploadFile) formData.append('imagem', uploadFile);
        else formData.append('imagem_url', novoProduto.imagem_url || '');

        try {
            if (editandoId) {
                await api.put(`/cardapio/produtos/${editandoId}`, formData);
                toast.success("Produto atualizado!");
            } else {
                await api.post(`/cardapio/produtos`, formData);
                toast.success("Produto cadastrado!");
            }
            fecharModal();
            carregarDados();
        } catch (e) { 
            toast.error(e.response?.data?.error || "Erro ao salvar produto."); 
        }
    };

    // Processamento de Importação em Massa (CSV, XLSX, PDF, Webhook)
    const handleImportarArquivo = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        const formData = new FormData();
        formData.append('estabelecimento_id', id);
        formData.append('tipo', importTipo);
        formData.append('arquivo', file);

        setImportandoLoading(true);
        try {
            await api.post(`/cardapio/produtos/importar`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            toast.success("Produtos importados e carregados automaticamente com sucesso!");
            setShowImportModal(false);
            carregarDados();
        } catch (err) {
            toast.error(err.response?.data?.error || "Erro ao processar arquivo de importação.");
        } finally {
            setImportandoLoading(false);
            if (importFileInputRef.current) importFileInputRef.current.value = '';
        }
    };

    const itemSelectorStyle = (isSelected) => ({
        display: 'flex',
        flexDirection: 'column',
        padding: '12px',
        marginBottom: '10px',
        borderRadius: '12px',
        background: isSelected ? '#eff6ff' : '#fff',
        border: isSelected ? '1px solid #3b82f6' : '1px solid #f1f5f9',
        cursor: 'pointer',
        transition: '0.2s'
    });

    return (
        <div className="dashboard-page">
            <Toaster position="top-right" />
            
            <header className="dashboard-header">
                <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                    <button onClick={() => navigate('/dashboard')} className="btn-icon-home"><Home size={20} /></button>
                    <div className="divider-vertical"></div>
                    <button onClick={() => navigate(-1)} className="btn-sair" style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <ChevronLeft size={18} /> Voltar
                    </button>
                </div>
                <h2 className="dashboard-logo">Gestão de <b>Produtos</b></h2>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <button onClick={() => setShowImportModal(true)} className="btn-secondary" style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#f8fafc', border: '1px solid #cbd5e1', cursor: 'pointer', padding: '8px 14px', borderRadius: '8px', fontWeight: '600' }}>
                        <UploadCloud size={16} /> Importar Produtos
                    </button>
                    <Link to={`/adicionais/${id}`} className="btn-secondary" style={{ textDecoration: 'none' }}>📦 Grupos & Regras</Link>
                </div>
            </header>

            <main className="dashboard-container">
                <div className="top-info" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div>
                        <h1>Produtos do Cardápio</h1>
                        <p style={{color: '#64748b'}}>Cadastre seus itens e vincule às regras de Marmitex ou Pizza.</p>
                    </div>
                    <button onClick={() => setShowModal(true)} className="btn-confirm">+ Novo Produto</button>
                </div>

                <div className="dashboard-grid" style={{marginTop: '20px'}}>
                    {!loading && produtos.map(prod => (
                        <div key={prod.id} className="card">
                            <div style={{display: 'flex', gap: '15px'}}>
                                <img src={prod.imagem_url || 'https://via.placeholder.com/150'} style={{width: '75px', height: '75px', borderRadius: '12px', objectFit: 'cover'}} alt={prod.nome} />
                                <div style={{flex: 1}}>
                                    <h3 style={{margin: 0, fontSize: '15px'}}>{prod.nome}</h3>
                                    <span className="badge-nicho" style={{fontSize: '10px'}}>{categorias.find(c => c.id === prod.categoria_id)?.nome}</span>
                                    <div style={{marginTop: '5px'}}><b>R$ {parseFloat(prod.preco).toFixed(2).replace('.', ',')}</b></div>
                                </div>
                            </div>
                            <div className="card-footer" style={{marginTop: '15px', display: 'flex', justifyContent: 'space-between'}}>
                                <button onClick={() => {
                                    setEditandoId(prod.id);
                                    setNovoProduto({
                                        ...prod, 
                                        preco: String(prod.preco).replace('.', ','), 
                                        adicionais_ids: prod.adicionais_ids || []
                                    });
                                    setImagePreview(prod.imagem_url);
                                    setShowModal(true);
                                }} className="btn-text">Configurar</button>
                                
                                <button onClick={() => {
                                    setItemParaExcluir(prod);
                                    setShowDeleteModal(true);
                                }} className="btn-delete-text" style={{color: '#ef4444'}}>Excluir</button>
                            </div>
                        </div>
                    ))}
                </div>
            </main>

            {/* MODAL DE IMPORTAÇÃO AUTOMÁTICA (CSV, XLSX, PDF, WEBHOOK) */}
            {showImportModal && (
                <div className="modal-overlay">
                    <div className="modal" style={{ maxWidth: '750px', maxHeight: '90vh', overflowY: 'auto' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                            <h2>📥 Importação Automática de Produtos</h2>
                            <button onClick={() => setShowImportModal(false)} className="btn-text" style={{ fontSize: '18px' }}>✕</button>
                        </div>

                        <p style={{ color: '#64748b', fontSize: '13px', marginBottom: '20px' }}>
                            Escolha o formato de origem para carregar seus produtos automaticamente para o cardápio.
                        </p>

                        {/* Seletores de Formato */}
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '10px', marginBottom: '20px' }}>
                            {[
                                { id: 'csv', label: 'CSV', icon: <FileText size={20} /> },
                                { id: 'xlsx', label: 'XLSX (Excel)', icon: <Table size={20} /> },
                                { id: 'pdf', label: 'PDF Cardápio', icon: <FileText size={20} /> },
                                { id: 'webhook', label: 'Webhook API', icon: <Webhook size={20} /> }
                            ].map(item => (
                                <div 
                                    key={item.id}
                                    onClick={() => setImportTipo(item.id)}
                                    style={{
                                        padding: '12px',
                                        borderRadius: '12px',
                                        border: importTipo === item.id ? '2px solid #ea1d2c' : '1px solid #e2e8f0',
                                        background: importTipo === item.id ? '#fef2f2' : '#fff',
                                        cursor: 'pointer',
                                        textAlign: 'center',
                                        fontWeight: '600',
                                        fontSize: '13px',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        gap: '6px'
                                    }}
                                >
                                    {item.icon}
                                    {item.label}
                                </div>
                            ))}
                        </div>

                        {/* Conteúdo dinâmico de orientação passo a passo */}
                        {importTipo !== 'webhook' ? (
                            <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
                                <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <HelpCircle size={16} color="#0284c7" /> Passo a passo e campos obrigatórios ({importTipo.toUpperCase()})
                                </h4>
                                <p style={{ fontSize: '12px', color: '#475569', margin: '0 0 10px 0' }}>
                                    Seu arquivo <b>.{importTipo}</b> deve conter as seguintes colunas na primeira linha (cabeçalho):
                                </p>
                                <ul style={{ margin: 0, paddingLeft: '20px', fontSize: '12px', color: '#334155', display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                    <li><b>nome</b>: Nome descritivo do produto (Obrigatório)</li>
                                    <li><b>descricao</b>: Ingredientes ou detalhes do prato</li>
                                    <li><b>preco</b>: Valor numérico (Ex: 29.90 ou 29,90) (Obrigatório)</li>
                                    <li><b>estoque</b>: Quantidade disponível (Opcional, padrão 0)</li>
                                    <li><b>categoria</b>: Nome exato ou ID da categoria correspondente</li>
                                </ul>

                                <div style={{ marginTop: '12px', background: '#fff', padding: '10px', borderRadius: '8px', border: '1px dashed #cbd5e1' }}>
                                    <span style={{ fontSize: '11px', fontWeight: '700', color: '#64748b' }}>EXEMPLO DE LINHA VÁLIDA:</span>
                                    <code style={{ display: 'block', fontSize: '11px', color: '#0f172a', marginTop: '4px', background: '#f1f5f9', padding: '6px', borderRadius: '4px' }}>
                                        nome, descricao, preco, estoque, categoria<br/>
                                        "Pizza Margherita", "Molho, mussarela e manjericão", 45.00, 10, "Pizzas"
                                    </code>
                                </div>
                            </div>
                        ) : (
                            <div style={{ background: '#f8fafc', padding: '15px', borderRadius: '12px', border: '1px solid #e2e8f0', marginBottom: '20px' }}>
                                <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                    <Webhook size={16} color="#0284c7" /> Sincronização Automática via Webhook
                                </h4>
                                <p style={{ fontSize: '12px', color: '#475569', margin: '0 0 8px 0' }}>
                                    Envie requisições <code>POST</code> automatizadas contendo o JSON de seus produtos diretamente para o endpoint de integração do seu estabelecimento:
                                </p>
                                <code style={{ display: 'block', fontSize: '11px', color: '#0f172a', background: '#f1f5f9', padding: '8px', borderRadius: '6px', wordBreak: 'break-all' }}>
                                    https://app.universomidiadigital.com.br/api/cardapio/webhook/produtos/{id}
                                </code>
                            </div>
                        )}

                        {importTipo !== 'webhook' && (
                            <div style={{ textAlign: 'center', padding: '30px', border: '2px dashed #cbd5e1', borderRadius: '16px', background: '#fff' }}>
                                <UploadCloud size={40} color="#94a3b8" style={{ marginBottom: '10px' }} />
                                <p style={{ fontSize: '13px', fontWeight: '600', color: '#334155', margin: '0 0 10px 0' }}>
                                    {importandoLoading ? 'Processando e importando produtos...' : `Clique para selecionar o arquivo .${importTipo.toUpperCase()}`}
                                </p>
                                <input 
                                    type="file" 
                                    ref={importFileInputRef} 
                                    accept={importTipo === 'csv' ? '.csv' : importTipo === 'xlsx' ? '.xlsx,.xls' : '.pdf'} 
                                    onChange={handleImportarArquivo} 
                                    disabled={importandoLoading}
                                    style={{ display: 'none' }}
                                />
                                <button 
                                    type="button" 
                                    onClick={() => importFileInputRef.current.click()} 
                                    disabled={importandoLoading}
                                    className="btn-confirm"
                                    style={{ background: '#ea1d2c', padding: '10px 20px' }}
                                >
                                    {importandoLoading ? 'Carregando...' : 'Selecionar Arquivo'}
                                </button>
                            </div>
                        )}

                        <div className="modal-buttons" style={{ marginTop: '20px' }}>
                            <button type="button" onClick={() => setShowImportModal(false)} className="btn-text">Fechar</button>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL SALVAR / EDITAR */}
            {showModal && (
                <div className="modal-overlay">
                    <div className="modal" style={{ maxWidth: '950px', maxHeight: '90vh', overflowY: 'auto' }}>
                        <h2 style={{ marginBottom: '25px' }}>{editandoId ? '⚙️ Editar Produto' : '✨ Novo Produto'}</h2>
                        <form onSubmit={handleSalvarProduto}>
                            <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '30px' }}>
                                
                                <div>
                                    <label className="label">Nome do Produto</label>
                                    <input className="input" value={novoProduto.nome} onChange={e => setNovoProduto({ ...novoProduto, nome: e.target.value })} required />
                                    
                                    <label className="label" style={{marginTop: '15px'}}>Descrição</label>
                                    <textarea className="input" rows="3" value={novoProduto.descricao} onChange={e => setNovoProduto({ ...novoProduto, descricao: e.target.value })} placeholder="Ex: Carne, arroz, feijão e fritas..."></textarea>

                                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', marginTop: '15px' }}>
                                        <div>
                                            <label className="label">Preço Base (R$)</label>
                                            <input className="input" value={novoProduto.preco} onChange={e => setNovoProduto({ ...novoProduto, preco: e.target.value })} placeholder="0,00" required />
                                        </div>
                                        <div>
                                            <label className="label">Categoria</label>
                                            <select className="input" value={novoProduto.categoria_id} onChange={e => setNovoProduto({...novoProduto, categoria_id: e.target.value})}>
                                                {categorias.map(cat => <option key={cat.id} value={cat.id}>{cat.nome}</option>)}
                                            </select>
                                        </div>
                                    </div>

                                    {!isBebida() ? (
                                        <>
                                            <label className="label" style={{ marginTop: '20px', display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                <ListChecks size={16} /> Vincular Grupos (Marmitex/Pizza)
                                            </label>
                                            <div style={{ border: '1px solid #e2e8f0', borderRadius: '12px', overflow: 'hidden', marginTop: '10px' }}>
                                                {['obrigatorio', 'adicional', 'informativo'].map((tipo) => (
                                                    <details key={tipo} className="accordion-item" open={tipo === 'obrigatorio'}>
                                                        <summary style={{ padding: '12px', background: '#f8fafc', cursor: 'pointer', fontWeight: '700', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between' }}>
                                                            <span style={{textTransform: 'capitalize'}}>{tipo === 'obrigatorio' ? '🍱 Escolha Obrigatória' : tipo === 'adicional' ? '🛒 Extras/Adicionais' : 'ℹ️ Informativos'}</span>
                                                            <ChevronDown size={16} />
                                                        </summary>
                                                        <div style={{ padding: '15px', maxHeight: '250px', overflowY: 'auto', background: '#fff' }}>
                                                            {gruposDisponiveis.filter(g => g.tipo === tipo).map(grupo => (
                                                                <div key={grupo.id} onClick={() => toggleGrupo(grupo.id)} style={itemSelectorStyle(novoProduto.adicionais_ids.includes(grupo.id))}>
                                                                    <div style={{display: 'flex', alignItems: 'center', gap: '10px'}}>
                                                                        <input type="checkbox" checked={novoProduto.adicionais_ids.includes(grupo.id)} readOnly />
                                                                        <div>
                                                                            <div style={{fontSize: '13px', fontWeight: 'bold'}}>{grupo.nome}</div>
                                                                            <small style={{color: '#64748b'}}>Regra: {grupo.minimo} a {grupo.maximo} itens</small>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            ))}
                                                        </div>
                                                    </details>
                                                ))}
                                            </div>
                                        </>
                                    ) : (
                                        <div style={{marginTop: '30px', padding: '15px', background: '#f0f9ff', borderRadius: '12px', border: '1px solid #bae6fd', textAlign: 'center'}}>
                                            <Coffee size={24} color="#0369a1" style={{margin: '0 auto 8px'}} />
                                            <p style={{fontSize: '11px', color: '#0369a1', margin: 0}}>Bebidas não possuem grupos de escolha.</p>
                                        </div>
                                    )}
                                </div>

                                <div style={{ textAlign: 'center' }}>
                                    <label className="label">Foto do Produto</label>
                                    <div onClick={() => fileInputRef.current.click()} style={{ height: '280px', border: '2px dashed #cbd5e1', borderRadius: '20px', overflow: 'hidden', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f8fafc' }}>
                                        {imagePreview ? <img src={imagePreview} style={{ width: '100%', height: '100%', objectFit: 'cover' }} alt="" /> : <Plus size={40} color="#94a3b8" />}
                                    </div>
                                    <input type="file" ref={fileInputRef} hidden onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (file) { setUploadFile(file); setImagePreview(URL.createObjectURL(file)); }
                                    }} />

                                    <div style={{marginTop: '20px', padding: '15px', background: '#f1f5f9', borderRadius: '12px', textAlign: 'left'}}>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer', marginBottom: '10px' }}>
                                            <input type="checkbox" checked={novoProduto.destaque === 1} onChange={e => setNovoProduto({ ...novoProduto, destaque: e.target.checked ? 1 : 0 })} />
                                            <span style={{fontWeight: '600', fontSize: '14px'}}>Produto em Destaque ★</span>
                                        </label>
                                        <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                                            <input type="checkbox" checked={novoProduto.ativo === 1} onChange={e => setNovoProduto({ ...novoProduto, ativo: e.target.checked ? 1 : 0 })} />
                                            <span style={{fontWeight: '600', fontSize: '14px'}}>Produto Ativo</span>
                                        </label>
                                    </div>
                                </div>
                            </div>

                            <div className="modal-buttons" style={{ marginTop: '30px' }}>
                                <button type="button" onClick={fecharModal} className="btn-text">Cancelar</button>
                                <button type="submit" className="btn-confirm" style={{background: '#ea1d2c'}}>Salvar Produto</button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* MODAL CONFIRMAR EXCLUSÃO */}
            {showDeleteModal && (
                <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
                    <div className="modal-confirm" onClick={e => e.stopPropagation()}>
                        <AlertTriangle size={40} color="#ea1d2c" style={{marginBottom: '10px'}} />
                        <h3>Excluir Produto?</h3>
                        <p>Deseja remover <b>{itemParaExcluir?.nome}</b>? Esta ação removerá o item do cardápio permanentemente.</p>
                        <div className="modal-buttons" style={{marginTop: '20px'}}>
                            <button onClick={() => setShowDeleteModal(false)} className="btn-text">Cancelar</button>
                            <button onClick={handleExcluirProduto} className="btn-confirm-delete" style={{background: '#ea1d2c', color: '#fff', border: 'none', padding: '10px 20px', borderRadius: '8px', cursor: 'pointer'}}>Confirmar Exclusão</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}